/** The three states a read has, and the one this product kept getting wrong — **EXP-29**.
 *
 * A query is loading, or it failed, or it answered. Thirteen screens rendered the **second as the
 * third**: `const { data: risks = [] } = useRisks()` turns a failed request into an empty array,
 * and an empty array renders as *"No risks on the register."* That sentence is a **claim about the
 * company** — it says somebody assessed their exposure and found none — and it is produced by a
 * dropped connection. A reader acts on it: they stop looking.
 *
 * The same shape, screen by screen: "No sources expected", "No documents yet", "Nothing to review",
 * "No filings". Every one of them is a product that forgot it asked, wearing the face of a product
 * that knows.
 *
 * So the three states are **one component**, and a screen opts out of none of them:
 *
 * - **Failed** — what failed, and a way to try again. `30-design-principles.md` rule 7: the
 *   sentence is for the person, not derived from a status code. A failure with no way out but a
 *   page reload is a dead end, and on a screen whose job is to be trusted about numbers a dead end
 *   reads as the product being broken rather than one request having failed.
 * - **Loading** — said, not blank. A blank panel and an empty register look identical.
 * - **Empty** — the screen's own sentence, which is the only one of the three a screen can write
 *   better than this component can: it is where the **next action** goes (EXP-29 again), and
 *   "No documents yet" is useless beside "Upload a bill or a statement to start".
 *
 * `make ui-check` aborts a screen's own request and asserts the screen says the read failed rather
 * than that there is nothing there.
 */

import type { ReactNode } from "react";

import { isAmbiguous, Refused } from "../api/refusal";
import { NotYourRole } from "./NotYourRole";
import { Empty } from "./Panel";
import styles from "./Loaded.module.css";

/** What this needs from a query, which is what every React Query result gives.
 *
 * Structural rather than `UseQueryResult<T>`: a screen that has already narrowed or merged two
 * queries can still pass its own object, and nothing here depends on the rest of that type.
 */
export interface ReadState<T> {
  data: T | undefined;
  isPending: boolean;
  error: unknown;
  refetch: () => void;
}

/** The words for a request that never completed. `fetch` rejects with a bare `TypeError` ("Failed to
 * fetch", "Load failed", "NetworkError when attempting to fetch resource"), each a browser's wording
 * and none a sentence for a person: what a reader needs is that the connection is the problem and
 * that asking again is the remedy (E2E P9, G-P9-23). */
const UNREACHABLE = "EmitIQ could not be reached. Check your connection, then try again.";

function sentence(error: unknown): string {
  if (error instanceof TypeError) return UNREACHABLE;
  const said =
    error instanceof Error && error.message ? error.message : "That could not be loaded.";
  // The reference, when the server gave one (gate 35). Appended here, once, so every failed read
  // in the product gets it without a screen remembering to print it.
  return error instanceof Refused && error.requestId
    ? `${said} (Reference ${error.requestId}.)`
    : said;
}

/** The failure state on its own, for a screen whose whole body is one read.
 *
 * `Sources` and `Reports` return early when their census or their report cannot be read, so they
 * cannot wrap a child in `Loaded` — and before this they rendered *"The census could not be
 * read"* with **no way out but a page reload**. Same sentence, same affordance, one implementation.
 */
export function ReadFailed({
  of,
  error,
  onRetry,
}: {
  of: string;
  error: unknown;
  onRetry: () => void;
}) {
  // **A refusal by role is not a failure, and asking again cannot change it** (Q-05, G-P6-02, -03):
  // the server said which roles may, so the screen does too and offers no retry.
  if (error instanceof Refused && error.isWrongRole) return <NotYourRole needs={error.needs} />;
  return (
    <div role="alert" data-testid="read-failed" className={styles.failed}>
      <p className={styles.failedText}>
        {of} could not be loaded.
        <br />
        {sentence(error)}
      </p>
      <button type="button" className={styles.retry} onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/** What a failed-read list needs from each read: the shape of a query result, structurally. */
export interface ReadStatus {
  error: unknown;
  isPending: boolean;
  refetch: () => unknown;
}

/** Every read in `reads` that failed, each as the shared `ReadFailed` with its own Try again - and
 * **nothing at all** when none did, so a caller writes it unconditionally.
 *
 * One home for a pattern that kept being written by hand, once per screen: a figure, a tile or a chart
 * is drawn from a read that is *not* the screen's main one, and when it fails the figure goes quiet.
 * A quiet figure with no sentence beside it is the product's recurring defect (EXP-29; E2E P9,
 * G-P9-10, -11, -22): the reader sees a dash and takes it for a value. The notices are an alert each, so
 * a screen reader hears them, and keyed by what failed, so two failures are two sentences. */
export function ReadsFailed({
  reads,
  testId,
}: {
  reads: ReadonlyArray<{ of: string; read: ReadStatus }>;
  testId?: string;
}) {
  const failed = reads.filter(({ read }) => Boolean(read.error));
  if (failed.length === 0) return null;
  return (
    <div data-testid={testId}>
      {failed.map(({ of, read }) => (
        <ReadFailed
          key={of}
          of={of}
          error={read.error}
          onRetry={() => {
            void read.refetch();
          }}
        />
      ))}
    </div>
  );
}

export function Loaded<T>({
  query,
  /** What to say when the read succeeded and there is nothing in it — **with the next action**. */
  empty,
  /** What the reader was looking at, for the failure sentence: "the risk register", "this filing". */
  of,
  children,
}: {
  query: ReadState<T>;
  empty?: ReactNode;
  of?: string;
  children: (data: NonNullable<T>) => ReactNode;
}) {
  if (query.error) {
    if (query.error instanceof Refused && query.error.isWrongRole) {
      return <NotYourRole needs={query.error.needs} />;
    }
    // `of` is optional, so a caller that did not name what it was reading gets the failure's own
    // sentence rather than "undefined could not be loaded".
    return of ? (
      <ReadFailed of={of} error={query.error} onRetry={() => query.refetch()} />
    ) : (
      <div role="alert" data-testid="read-failed" className={styles.failed}>
        <p className={styles.failedText}>{sentence(query.error)}</p>
        <button type="button" className={styles.retry} onClick={() => query.refetch()}>
          Try again
        </button>
      </div>
    );
  }
  if (query.isPending) {
    return <LoadingRows />;
  }
  if (query.data === undefined || query.data === null || isEmptyList(query.data)) {
    // **A screen without its own sentence gets a dull true one, never a cheerful false one.** This
    // branch exists because `empty` is optional, and a panel that rendered nothing here would look
    // like a panel still loading.
    return <Empty>{empty ?? "Nothing here yet."}</Empty>;
  }
  return <>{children(query.data)}</>;
}

/** A loading state shaped like a register: five rows of a name, a bar and a short figure.
 *
 * Not a spinner (33-ui-build-spec, States) - a spinner says "wait" and a skeleton says "a list of
 * about this size is coming", which is what stops the panel jumping when it lands. The words are
 * real text, visually hidden: "Loading…" is what a screen reader hears and what the screen-state
 * tests look for to prove a screen is not stuck, and a skeleton alone says neither. */
function LoadingRows() {
  return (
    <div className={styles.loading} role="status" aria-busy="true">
      <span className="visuallyHidden">Loading…</span>
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className={styles.loadingRow} aria-hidden="true">
          <span className={`skeleton ${styles.name}`} />
          <span className={`skeleton ${styles.mid}`} />
          <span className={`skeleton ${styles.figure}`} />
        </div>
      ))}
    </div>
  );
}

function isEmptyList(data: unknown): boolean {
  return Array.isArray(data) && data.length === 0;
}

/** A **write** that was refused, said in the words its own refusal earns — EXP-29's second list.
 *
 * `ReadFailed` above is the read half and it has one remedy, because a read has one: ask again. A
 * write has four, and the whole of EXP-29's second list is that the product offered one:
 *
 * - **Unauthorized.** Nothing was written and asking again will fail identically. So there is no
 *   retry, and the sentence is the server's — written once, server-side, so two screens cannot
 *   describe the same permission differently (design rule 7). A "Try again" here is the same
 *   defect as a button that predictably 403s, one step later.
 * - **Stale.** Somebody else moved the row since this screen read it. The remedy is **reload and
 *   look at what changed**, which is the one a generic failure never offers — and `expected_version`
 *   exists on three routes precisely so this case is distinguishable. Retrying the same request
 *   would either fail again or, worse, succeed against a decision nobody reviewed.
 * - **Unknown.** `fetch` rejected, or a proxy answered 502/503/504 for an application that may well
 *   have finished the work. EXP-35's canonical case: *a request that timed out at a proxy and
 *   succeeded*. The honest sentence says the outcome is unknown and sends the reader to **look**,
 *   because a cheerful "try again" on a write that already landed is how a figure gets doubled.
 *   Where the server can answer a retry itself — `POST /evidence/{id}/import` answers
 *   `already_imported` before the state machine, and the spreadsheet apply derives an idempotency
 *   key per row — the screen says so, via `retrySafe`.
 * - **Anything else.** The server's sentence and a retry, which is what every one of these used to
 *   be.
 *
 * It renders **nothing** for no error, so a caller writes it unconditionally rather than guarding
 * it — a guard is where the next person forgets a branch.
 */
export function WriteFailed({
  error,
  of,
  onReload,
  onRetry,
  retrySafe,
  testId,
  className,
  nowReads,
  onReapply,
  reapplying,
}: {
  error: unknown;
  /** What was being written: "that task", "this decision". Used in the unknown-outcome sentence,
   * where a reader has to be told **what** to go and check. */
  of: string;
  /** Re-read what this screen is looking at. Offered for a stale refusal and an unknown outcome —
   * the two cases where the screen's own copy of the row is the thing that is wrong. */
  onReload?: () => void;
  /** Send the same request again. Deliberately **not** offered for an unauthorized refusal or an
   * unknown outcome. */
  onRetry?: () => void;
  /** Whether sending this request twice is answered by the server rather than duplicated (EXP-35,
   * ADR-0039). Only the caller knows, and claiming it where it is not true is worse than saying
   * nothing — so it is opt-in and the sentence changes rather than the button appearing. */
  retrySafe?: boolean;
  /** A screen's own `data-testid`, where the gate already selects one. The **branch** is a
   * separate attribute (`data-branch`), so overriding the id does not cost an assertion: a check
   * can name the element and still say which of the four refusals it expected. */
  testId?: string;
  /** A screen's own class, for a form whose refusal is laid out by that form. Added to this
   * component's own rather than replacing it. */
  className?: string;
  /** **What the row now says**, read again after a stale refusal (G-P9-04), for a form that keeps what
   * the person typed. With it the stale branch names that someone else changed the row and what it
   * now reads, and says the typed value is kept. Absent, the stale branch is the generic one. */
  nowReads?: ReactNode;
  /** Send what the person typed again against the version the row is on now. Offered only with a
   * stale refusal and only by a form that holds the typed value - "Reload and re-apply" - because the
   * plain reload would throw it away. */
  onReapply?: () => void;
  /** The re-apply is in flight. */
  reapplying?: boolean;
}) {
  if (!error) return null;

  const refusal = error instanceof Refused ? error : null;
  const sentence =
    error instanceof Error && error.message ? error.message : `${of} could not be saved.`;

  const unknown = isAmbiguous(error);
  const branch = unknown
    ? "unknown"
    : refusal?.isStale
      ? "stale"
      : refusal?.isUnauthorized
        ? "unauthorized"
        : "refused";

  return (
    <p
      className={[styles.refused, unknown && styles.unknown, className]
        .filter(Boolean)
        .join(" ")}
      role="alert"
      data-testid={testId ?? `write-${branch}`}
      data-branch={branch}
    >
      {branch === "unknown" ? (
        <>
          <strong className={styles.refusedHead}>It is not known whether that was saved.</strong>
          The request did not complete, which is not the same as it not having happened — a request
          that times out on the way back has usually already been carried out.
          <span className={styles.refusedNext}>
            {retrySafe
              ? `Reload and look at ${of}. Sending it again is safe either way: EmitIQ answers a repeat rather than recording it twice.`
              : `Reload and look at ${of} before sending it again, so you do not record it twice.`}
          </span>
        </>
      ) : branch === "stale" && onReapply ? (
        <>
          <strong className={styles.refusedHead}>
            This changed while you were looking at it.
          </strong>
          Someone else changed {of} since you opened it.
          {nowReads ? (
            <span className={styles.refusedNext} data-testid="write-now-reads">
              It now reads {nowReads}.
            </span>
          ) : null}
          <span className={styles.refusedNext}>What you typed is still here.</span>
        </>
      ) : branch === "stale" ? (
        <>
          <strong className={styles.refusedHead}>
            This changed while you were looking at it.
          </strong>
          {sentence}
          {refusal?.currentVersion === null || refusal?.currentVersion === undefined ? null : (
            <span className={styles.refusedNext}>
              It is now on version {refusal.currentVersion}. Nothing you did was saved, and nothing
              somebody else did was lost.
            </span>
          )}
        </>
      ) : branch === "unauthorized" ? (
        <>
          <strong className={styles.refusedHead}>You cannot do this.</strong>
          {sentence}
        </>
      ) : (
        sentence
      )}

      {/* **The remedy, and only the one this refusal earns.** No retry for an unauthorized
          refusal — asking again fails identically, which is a control that predictably fails — and
          none for an unknown outcome, where re-sending could record the same thing twice. */}
      {branch === "stale" && onReapply ? (
        <button
          type="button"
          className={styles.retry}
          data-testid="write-reapply"
          disabled={reapplying}
          onClick={onReapply}
        >
          {reapplying ? "Re-applying…" : "Reload and re-apply"}
        </button>
      ) : (branch === "unknown" || branch === "stale") && onReload ? (
        <button
          type="button"
          className={styles.retry}
          data-testid="write-reload"
          onClick={onReload}
        >
          Reload it
        </button>
      ) : null}
      {branch === "refused" && onRetry ? (
        <button type="button" className={styles.retry} onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </p>
  );
}
